<template>
  <div class="map-container">
    <geocoder
      v-if="isReady"
      :map="instance"
      @add-marker-on-map="addMarkerOnMap"
    />
    <custom-menu
      :list="stylesList"
      :current-map-style="currentMapStyle"
      @change-map-style="changeMapStyle"
    />
    <div ref="mapElement" class="map-container__region">
      <slot v-if="isReady" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Mapbox from 'mapbox-gl';
import isClosedHost from '@/utils/is-closed-host';
import Geocoder from '@/components/common/geocoder';
import CustomMenu from '@/components/ui/custom-menu';
import {
  PUBLIC_MAP_STYLE_LIST,
  PRIVATE_MAP_STYLE_LIST,
} from '@/utils/map-box/map-styles/map-styles';
import parseJson from '@/utils/parseJson';

const DEFAULT_ZOOM = 7;
const DEFAULT_CENTER = [37.61822, 54.19609];

export default {
  name: 'map-view',

  async asyncData({ store }) {
    return store.dispatch('wms/getList');
  },

  components: { Geocoder, CustomMenu },

  props: {
    compared: Boolean,
    fit: Array,
  },

  data: () => ({
    isReady: false,
    instance: {},
    stylesList: [],
    currentMapStyle: 'default',
  }),

  computed: {
    ...mapState('wms', { wmsList: 'list' }),
  },

  async mounted() {
    this.stylesList = isClosedHost() ? PRIVATE_MAP_STYLE_LIST : PUBLIC_MAP_STYLE_LIST;
    await this.$store.dispatch('wms/getList');
    this.joinMapStyles();

    Mapbox.accessToken = 'pk.eyJ1Ijoicm9tYXZlcm5lIiwiYSI6ImNqdHRzZGlkajA3YmE0NHF5aWR5bzM0MDMifQ.RIrCtXnxBaev-IbOSLqn4g';
    this.instance = new Mapbox.Map({
      container: this.$refs.mapElement,
      antialias: true,
      style: this.getMapStyle('default'),
      attributionControl: false,
      center: DEFAULT_CENTER,
      pitch: 0,
      bearing: 0,
      zoom: DEFAULT_ZOOM,
      fadeDuration: 0,
    });

    this.fireEvents();

    if (this.fit && this.fit.length) {
      this.instance.fitBounds([
        this.fit[0][0],
        this.fit[0][2],
      ]);
    }
  },

  beforeDestroy() {
    this.$emit('destroy');
  },

  methods: {
    addMarkerOnMap(coordinates) {
      this.$emit('add-marker-on-map', coordinates);
    },

    joinMapStyles() {
      if (this.wmsList) {
        const wmsList = this.wmsList.map((item) => ({
          key: item.id,
          title: item.name,
          style: parseJson(item.style),
        }));

        this.stylesList = [
          ...this.stylesList,
          ...wmsList,
        ];
      }
    },

    getMapStyle(styleKey) {
      return this.stylesList.find(item => item.key === styleKey).style;
    },

    changeMapStyle(item) {
      this.instance.setStyle(item.style);
      this.currentMapStyle = item.key;
      this.$emit('style-change', this.instance);
    },

    fireEvents() {
      this.instance.once('load', () => {
        this.instance.resize();
        this.isReady = true;
        this.$emit('created', this.instance);
      });
    },
  },
};
</script>

<style lang="css" src="mapbox-gl/dist/mapbox-gl.css"></style>
<style lang="css" src="@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css"></style>

<style lang="css">

a.mapboxgl-ctrl-logo {
  display: none;
}
</style>

<style lang="scss" scoped>

.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &__region {
    position: absolute;
    width: 100%;
    height: 100%;
  }
}
</style>
